import React, { useEffect, useMemo, useState } from "react";
import { Form, Modal } from "antd";
import ScheduleDraftFlightForm from "./ScheduleDraftFlightForm";
import ScheduleDraftRoutePicker from "./ScheduleDraftRoutePicker";
import { DEFAULT_STC_TYPE } from "../constants/scheduleDraftOptions";
import { getTrackFlights } from "../lib/trackUtils";

function getCurrentTrack(draft, currentEditingTrackId) {
    return (draft?.tracks || []).find((track) => String(track.id) === String(currentEditingTrackId)) || null;
}

function getDefaultAircraftReg({ insertionContext, flights, draft }) {
    const anchorFlight = insertionContext?.anchorFlight || insertionContext?.anchor_flight;
    if (anchorFlight?.aircraft_reg) {
        return anchorFlight.aircraft_reg;
    }
    const lastFlightWithAircraft = [...(flights || [])].reverse().find((flight) => flight?.aircraft_reg);
    return lastFlightWithAircraft?.aircraft_reg || draft?.aircraft_scope?.[0] || "";
}

const ScheduleDraftAddFlightModal = ({ open, onCancel, onSubmit, confirmLoading, currentEditingTrackId, draftId, draft, insertionContext, airportConstraintsMap }) => {
    const [form] = Form.useForm();
    const [mode, setMode] = useState("quick");

    useEffect(() => {
        if (open) {
            setMode("quick");
        }
    }, [open]);

    const currentTrack = useMemo(() => getCurrentTrack(draft, currentEditingTrackId), [currentEditingTrackId, draft]);
    const currentTrackFlights = useMemo(() => getTrackFlights(currentTrack), [currentTrack]);
    const defaultAircraftReg = useMemo(
        () => getDefaultAircraftReg({ insertionContext, flights: currentTrackFlights, draft }),
        [currentTrackFlights, draft, insertionContext],
    );

    const handleOk = async () => {
        try {
            const values = await form.validateFields();
            await onSubmit({
                ...values,
                insertionContext,
                track_id: currentEditingTrackId || values.track_id,
            });
            form.resetFields();
        } catch (error) {
            if (error?.errorFields) {
                return;
            }
        }
    };

    const handleCancel = () => {
        form.resetFields();
        setMode("quick");
        onCancel();
    };

    const isAdvancedMode = mode === "advanced";

    return (
        <Modal
            title={isAdvancedMode ? "新增航班（高级模式）" : "添加航班"}
            open={open}
            onCancel={handleCancel}
            onOk={isAdvancedMode ? handleOk : undefined}
            okText={isAdvancedMode ? "创建航班" : undefined}
            cancelText={isAdvancedMode ? "取消" : undefined}
            confirmLoading={confirmLoading}
            footer={isAdvancedMode ? undefined : null}
            width={isAdvancedMode ? 520 : 760}
            destroyOnClose
        >
            {isAdvancedMode ? (
                <Form form={form} layout="vertical" initialValues={{ is_fixed: false, stc_type: DEFAULT_STC_TYPE }}>
                    <ScheduleDraftFlightForm editable={true} />
                </Form>
            ) : (
                <ScheduleDraftRoutePicker
                    draftId={draftId}
                    draft={draft}
                    currentEditingTrackId={currentEditingTrackId}
                    defaultAircraftReg={defaultAircraftReg}
                    flights={currentTrackFlights}
                    insertionContext={insertionContext}
                    airportConstraintsMap={airportConstraintsMap}
                    onSubmit={onSubmit}
                    onCancel={handleCancel}
                    onAdvancedMode={() => setMode("advanced")}
                    confirmLoading={confirmLoading}
                />
            )}
        </Modal>
    );
};

export default ScheduleDraftAddFlightModal;
